"use client"

import * as React from "react"
import { format, parseISO } from "date-fns"
import { Archive, Trash2, Send, Copy } from "lucide-react"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"
import { isInterimMessage } from "@/lib/interim-messages"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { PauseControl } from "./pause-control"
import { conversationsApi, ConversationsApiError } from "@/services/conversationsApi"
import { useToast } from "@/hooks/use-toast"
import { ConfirmationDialog } from "@/components/shared/confirmation-dialog"
import { useRealtimeNotifications } from "@/hooks/use-realtime-notifications"
import { useSelectedConversation } from "@/hooks/use-selected-conversation"
import { useIsMobile } from "@/hooks/use-mobile"
import { MessageAttachments } from "./message-attachments/MessageAttachments"
import { ContactAvatar } from "./contact-avatar"
import { WhatsAppLineSwitch } from "./whatsapp-line-switch"
import { ConversionEventDialog } from "./conversion-event-dialog"
import { MessengerOutreachActions } from "./messenger-outreach-actions"
import type { MessageAttachment } from "@/types/conversations"
import type { Mail } from "@/lib/data"
import { notifyChannelConnectionChanged } from "@/lib/channel-connection-events"

function formatTimeRemaining(seconds: number): string {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const remainingSeconds = seconds % 60
  return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${remainingSeconds.toString().padStart(2, "0")}`
}

function getBadgeStyles(isPaused: boolean) {
  if (isPaused) {
    return "bg-warning/10 text-warning hover:bg-warning/20 px-3 py-1"
  }
  return "bg-primary/10 text-primary hover:bg-primary/20 px-3 py-1"
}

interface Message {
  id: string
  text: string | null
  sender: 'user' | 'assistant' | 'admin' | 'bot'
  status?: string
  timestamp: string
  metadata?: Record<string, unknown>
  attachments?: MessageAttachment[]
  is_analyzing?: boolean
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null
}

function getDeliveryFailure(metadata: Record<string, unknown> | undefined): {
  code?: string
  title?: string
  message?: string
  isTemplate: boolean
} | null {
  if (!metadata || metadata.delivery_status !== 'failed') {
    return null
  }

  const deliveryError = asRecord(metadata.delivery_error)

  return {
    code: typeof deliveryError?.code === 'number' || typeof deliveryError?.code === 'string'
      ? String(deliveryError.code)
      : undefined,
    title: typeof deliveryError?.title === 'string' ? deliveryError.title : undefined,
    message: typeof deliveryError?.message === 'string' ? deliveryError.message : undefined,
    isTemplate: metadata.is_template === true,
  }
}

function getDeliveryBadge(metadata: Record<string, unknown> | undefined): {
  label: string
  className: string
} | null {
  if (!metadata) {
    return null
  }

  switch (metadata.delivery_status) {
    case 'sent':
      return {
        label: 'Sent',
        className: 'border-border bg-muted text-muted-foreground',
      }
    case 'delivered':
      return {
        label: 'Delivered',
        className: 'border-primary/20 bg-primary/10 text-primary',
      }
    case 'read':
      return {
        label: 'Read',
        className: 'border-success/20 bg-success/10 text-success',
      }
    default:
      return null
  }
}

const isMockConversationId = (conversationId?: string | null): boolean =>
  Boolean(conversationId && conversationId.startsWith('mock-'))

const buildMockMessages = (): Message[] => ([
  {
    id: 'mock-message-1',
    text: 'Hola, ¿tienes disponibilidad para ver el piso esta semana?',
    sender: 'user',
    timestamp: new Date(Date.now() - 8 * 60 * 1000).toISOString(),
    attachments: [],
    is_analyzing: false,
  },
  {
    id: 'mock-message-2',
    text: 'Claro. Tengo huecos el miércoles a las 16:30 y 17:30. ¿Cuál prefieres?',
    sender: 'assistant',
    timestamp: new Date(Date.now() - 7 * 60 * 1000).toISOString(),
    attachments: [],
    is_analyzing: false,
  },
  {
    id: 'mock-message-3',
    text: '17:30 me viene bien.',
    sender: 'user',
    timestamp: new Date(Date.now() - 6 * 60 * 1000).toISOString(),
    attachments: [],
    is_analyzing: false,
  },
  {
    id: 'mock-message-4',
    text: 'Perfecto, te dejo la visita pre-agendada para el miércoles a las 17:30. ¿Quieres que te envíe también ubicación y detalles?',
    sender: 'assistant',
    timestamp: new Date(Date.now() - 5 * 60 * 1000).toISOString(),
    attachments: [],
    is_analyzing: false,
  },
])

// Props for ConversationDisplay component
interface ConversationDisplayProps {
  conversation: Mail | null
  translations?: (key: string) => string
  onDeleted?: (conversationId: string) => void
  onArchived?: (conversationId: string) => void
  onActivated?: (conversationId: string) => void
  markAsRead?: (conversationId: string) => Promise<void>
  onPhoneCallArchived?: () => void
}

export function ConversationDisplay({ conversation: mail, translations, onDeleted, onArchived, onActivated, markAsRead, onPhoneCallArchived }: ConversationDisplayProps) {
  const { toast } = useToast()
  const [, setSelectedConversation] = useSelectedConversation()
  const isMobile = useIsMobile()

  // Translation helper function
  const t = React.useCallback((key: string) => {
    if (!translations) return key
    return translations(key)
  }, [translations])

  // State for conversation messages
  const [messages, setMessages] = React.useState<Message[]>([])
  const [loadingMessages, setLoadingMessages] = React.useState(false)
  const [messagesError, setMessagesError] = React.useState<string | null>(null)
  const [messageInput, setMessageInput] = React.useState("")
  const [sendingMessage, setSendingMessage] = React.useState(false)

  // State for chatbot pause functionality
  const [isPaused, setIsPaused] = React.useState(false)
  const [pauseEndTime, setPauseEndTime] = React.useState<Date | null>(null)
  const [remainingPauseTime, setRemainingPauseTime] = React.useState<number | null | typeof Infinity>(null)
  const [isLoadingPauseStatus, setIsLoadingPauseStatus] = React.useState(false)
  const [isArchiving, setIsArchiving] = React.useState(false)
  const [isDeleting, setIsDeleting] = React.useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false)
  const isMockConversation = isMockConversationId(mail?.id)

  // User intentions removed from here - now shown in Profile tab

  // Define interface for phone call turns
  interface PhoneCallTurn {
    id: string
    message: string
    role: 'user' | 'assistant'
    created_at: string
  }

  // Define interface for phone call messages response
  interface PhoneCallMessagesResponse {
    turns: {
      data: PhoneCallTurn[]
    }
  }

  // Transform phone call turns to messages format
  const transformPhoneCallTurnsToMessages = React.useCallback((turns: PhoneCallTurn[]): Message[] => {
    return turns.map((turn, index) => ({
      id: turn.id || `turn-${index}`,
      text: turn.message || '',
      sender: turn.role === 'user' ? 'user' : 'assistant',
      timestamp: turn.created_at || new Date().toISOString(),
      attachments: [],
      is_analyzing: false
    }))
  }, [])

  const loadMessages = React.useCallback(async (conversationId: string) => {
    setLoadingMessages(true)
    setMessagesError(null)
    try {
      if (isMockConversationId(conversationId)) {
        setMessages(buildMockMessages())
        return
      }

      let messages: Message[] = []

      // Check if this is a phone call by looking at the mail platform
      if (mail?.platform === 'phone_call') {
        // Use phone call endpoint
        const response = await conversationsApi.getPhoneCallMessages(conversationId) as PhoneCallMessagesResponse
        // Transform turns to messages format
        messages = transformPhoneCallTurnsToMessages(response.turns?.data || [])
      } else {
        // Use regular conversation endpoint
        const response = await conversationsApi.getConversationMessages(conversationId)
        messages = Array.isArray(response) ? response : []
      }

      setMessages(messages)
    } catch (error) {
      setMessages([])
      setMessagesError(error instanceof Error ? error.message : 'Error loading messages')
    } finally {
      setLoadingMessages(false)
    }
  }, [mail?.platform, transformPhoneCallTurnsToMessages])

  // Ref for scroll area to control scroll position
  const scrollAreaRef = React.useRef<HTMLDivElement>(null)

  // Function to scroll to bottom smoothly
  const scrollToBottom = React.useCallback(() => {
    if (scrollAreaRef.current) {
      const scrollContainer = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]')
      if (scrollContainer) {
        scrollContainer.scrollTo({
          top: scrollContainer.scrollHeight,
          behavior: 'smooth'
        })
      }
    }
  }, [])

  // WebSocket integration for real-time message updates
  useRealtimeNotifications({
    conversationId: isMockConversation ? undefined : mail?.id,
    onNewMessage: React.useCallback((conversationId: string, message: unknown) => {
      // Only update if this is for the currently selected conversation
      if (mail?.id === conversationId) {
        const msg = message as {
          message?: unknown
          sender?: string
          timestamp?: string
          message_id?: string
          message_type?: string
          attachments?: MessageAttachment[]
        }
        if ((msg.message_type || 'chat_message') !== 'chat_message') {
          return
        }
        if (typeof msg.message !== 'string') {
          return
        }
        const incomingText = msg.message

        // Check if we already have this message to avoid duplicates
        setMessages(prev => {
          if (msg.message_id && prev.some(msgItem => msgItem.id === msg.message_id)) {
            return prev
          }
          const messageExists = prev.some(msgItem =>
            msgItem.text === incomingText &&
            msgItem.sender === msg.sender &&
            Math.abs(new Date(msgItem.timestamp).getTime() - new Date(msg.timestamp || new Date()).getTime()) < 10000 // Increased to 10 seconds
          )

          if (messageExists) {
            return prev
          }

          // Add the new message to the messages state
          const newMessage: Message = {
            id: msg.message_id || Date.now().toString(),
            text: incomingText,
            sender: (msg.sender as 'user' | 'assistant' | 'admin' | 'bot') || 'user',
            timestamp: msg.timestamp || new Date().toISOString(),
            attachments: Array.isArray(msg.attachments) ? msg.attachments : [],
            is_analyzing: false
          }


          // Add message directly and scroll smoothly
          setTimeout(scrollToBottom, 50)

          return [...prev, newMessage]
        })
      }
    }, [mail?.id, scrollToBottom]),
    onAttachmentUpdate: React.useCallback((messageId: string, updatedAttachment: MessageAttachment) => {
      setMessages(prev =>
        prev.map(msg => {
          if (msg.id !== messageId) {
            return msg
          }
          const current = Array.isArray(msg.attachments) ? msg.attachments : []
          const exists = current.some(attachment => attachment.id === updatedAttachment.id)
          if (exists) {
            return {
              ...msg,
              attachments: current.map(attachment =>
                attachment.id === updatedAttachment.id ? updatedAttachment : attachment
              )
            }
          }
          return {
            ...msg,
            attachments: [...current, updatedAttachment]
          }
        })
      )
    }, []),
    onConversationUpdate: React.useCallback((conversationId: string) => {
      if (mail?.id === conversationId) {
        // Reload messages to get updated data
        if (mail?.id) {
          loadMessages(mail.id)
        }
      }
    }, [mail?.id, loadMessages]),
  })

  // Load messages when mail changes
  React.useEffect(() => {
    if (mail?.id) {
      loadMessages(mail.id)
    } else {
      setMessages([])
      setMessagesError(null)
    }
  }, [mail?.id, mail?.subject, loadMessages])

  // Mark conversation as read when opened
  React.useEffect(() => {
    if (mail?.id && mail?.platform !== 'phone_call') {
      if (isMockConversationId(mail.id)) return
      if (markAsRead) {
        markAsRead(mail.id).catch((error) => {
          console.warn('Failed to mark conversation as read via hook:', error)
        })
      } else {
        conversationsApi.markAsRead(mail.id).catch((error) => {
          // Non-critical: log but don't show user error
          console.warn('Failed to mark conversation as read:', error)
        })
      }
    }
  }, [mail?.id, mail?.platform, markAsRead])

  // User intentions loading removed - now handled in Profile tab

  // Auto-scroll to bottom when messages change
  React.useEffect(() => {
    if (messages.length > 0) {
      // Small delay to ensure DOM is updated
      setTimeout(scrollToBottom, 150)
    }
  }, [messages, scrollToBottom])

  // Update remaining pause time
  React.useEffect(() => {
    if (!isPaused || !pauseEndTime || pauseEndTime === null) {
      setRemainingPauseTime(null)
      return
    }

    if (remainingPauseTime === Number.POSITIVE_INFINITY) {
      return
    }

    const interval = setInterval(() => {
      const now = new Date()
      const remaining = Math.max(0, Math.floor((pauseEndTime.getTime() - now.getTime()) / 1000))

      if (remaining <= 0) {
        setIsPaused(false)
        setPauseEndTime(null)
        setRemainingPauseTime(null)
      } else {
        setRemainingPauseTime(remaining)
      }
    }, 1000)

    return () => clearInterval(interval)
  }, [isPaused, pauseEndTime, remainingPauseTime])

  const handleSendMessage = async () => {
    if (!messageInput.trim() || !mail?.id || sendingMessage) return
    if (mail?.canReply === false) {
      toast({
        title: 'WhatsApp conversation is read-only',
        description: mail.replyBlockReason || (mail.phoneNumberId
          ? t('conversations.readonly_disconnected')
          : t('conversations.readonly_no_number')),
        variant: 'destructive',
      })
      return
    }

    const messageText = messageInput.trim()
    setSendingMessage(true)

    // Clear input immediately
    setMessageInput("")

    // Scroll to bottom when sending message
    setTimeout(scrollToBottom, 100)

    if (isMockConversationId(mail.id)) {
      const now = new Date()
      const userMessage: Message = {
        id: `mock-user-${now.getTime()}`,
        text: messageText,
        sender: 'admin',
        timestamp: now.toISOString(),
        attachments: [],
        is_analyzing: false,
      }
      setMessages((prev) => [...prev, userMessage])
      window.setTimeout(() => {
        setMessages((prev) => [
          ...prev,
          {
            id: `mock-assistant-${Date.now()}`,
            text: 'Mock: respuesta generada para validar UI mobile. Si quieres, conectamos este flujo al backend real.',
            sender: 'assistant',
            timestamp: new Date().toISOString(),
            attachments: [],
            is_analyzing: false,
          },
        ])
      }, 600)
      setSendingMessage(false)
      return
    }

    try {
      // Send as admin (agent)
      await conversationsApi.sendMessage(mail.id, messageText, 'admin')

      // Pause the AI automatically after sending a message as agent (only if not already paused)
      if (!isPaused) {
        await conversationsApi.pauseChatbot(mail.id, "indefinite")
        setIsPaused(true)
        setPauseEndTime(null)
        setRemainingPauseTime(Number.POSITIVE_INFINITY)
      }
      // If already paused, keep it paused (no action needed)

      // WebSocket will handle the message update automatically
      // No need for temp messages or timeouts

    } catch (error) {
      if (error instanceof ConversationsApiError && error.status === 409 && mail?.platform === 'whatsapp') {
        notifyChannelConnectionChanged({ channel: 'whatsapp', connected: false })
      }
      toast({
        title: t('functions.send_error') || 'Failed to send message',
        description: error instanceof Error ? error.message : String(error),
        variant: 'destructive'
      })
    } finally {
      setSendingMessage(false)
    }
  }

  const handlePause = async (duration: number) => {
    if (!mail?.id || isLoadingPauseStatus) return

    setIsLoadingPauseStatus(true)
    try {
      let durationMinutes: number | "indefinite"
      if (duration === Number.POSITIVE_INFINITY) {
        durationMinutes = "indefinite"
      } else {
        durationMinutes = duration
      }

      await conversationsApi.pauseChatbot(mail.id, durationMinutes)

      // Update local state based on response
      setIsPaused(true)
      if (duration === Number.POSITIVE_INFINITY) {
        setPauseEndTime(null)
        setRemainingPauseTime(Number.POSITIVE_INFINITY)
      } else {
        const endTime = new Date(Date.now() + duration * 60000)
        setPauseEndTime(endTime)
        setRemainingPauseTime(duration * 60)
      }

      toast({ title: t('functions.paused_indefinitely') || 'Automation paused indefinitely' })
    } catch (error) {
      toast({
        title: t('functions.pause_error') || 'Failed to pause automation',
        description: error instanceof Error ? error.message : String(error),
        variant: 'destructive'
      })
    } finally {
      setIsLoadingPauseStatus(false)
    }
  }

  const handleResume = async () => {
    if (!mail?.id || isLoadingPauseStatus) return

    setIsLoadingPauseStatus(true)
    try {
      await conversationsApi.resumeChatbot(mail.id)

      // Update local state
      setIsPaused(false)
      setPauseEndTime(null)
      setRemainingPauseTime(null)

      toast({ title: t('functions.resumed') || 'Automation resumed' })
    } catch (error) {
      toast({
        title: t('functions.resume_error') || 'Failed to resume automation',
        description: error instanceof Error ? error.message : String(error),
        variant: 'destructive'
      })
    } finally {
      setIsLoadingPauseStatus(false)
    }
  }

  const handleCopyConversationId = async () => {
    if (!mail?.id) return

    try {
      // Try modern clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(mail.id)
        toast({ 
          title: t('functions.conversation_id_copied') || 'Conversation ID copied to clipboard' 
        })
      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea')
        textArea.value = mail.id
        textArea.style.position = 'fixed'
        textArea.style.left = '-999999px'
        document.body.appendChild(textArea)
        textArea.select()
        try {
          document.execCommand('copy')
          toast({ 
            title: t('functions.conversation_id_copied') || 'Conversation ID copied to clipboard' 
          })
        } catch {
          toast({
            title: 'Failed to copy conversation ID',
            variant: 'destructive'
          })
        } finally {
          document.body.removeChild(textArea)
        }
      }
    } catch (error) {
      toast({
        title: t('functions.conversation_id_copied') || 'Failed to copy conversation ID',
        description: error instanceof Error ? error.message : String(error),
        variant: 'destructive'
      })
    }
  }

  // Transform API messages to display format
  const conversation = React.useMemo(() => {
    if (!messages.length) {
      return []
    }

    return messages.map((message) => {
      const isUser = message.sender === 'user'
      const isAdmin = message.sender === 'admin'

      return {
        role: isUser ? 'user' : 'assistant',
        name: isUser
          ? (mail?.name || 'Usuario')
          : isAdmin
            ? t('functions.agent')
            : t('functions.ai_assistant'),
        email: isUser ? (mail?.email || '') : '',
        isUserMessage: isUser,
        assistantAvatarSrc: !isUser
          ? isAdmin
            ? '/human-agent-avatar.png'
            : '/ai-avatar.png'
          : undefined,
        id: message.id,
        status: message.status,
        metadata: message.metadata,
        attachments: message.attachments ?? [],
        content: message.text ?? '',
        timestamp: message.timestamp ? parseISO(message.timestamp) : new Date()
      }
    })
  }, [messages, mail?.name, mail?.email, t])

  const isReadOnlyWhatsAppConversation =
    mail?.platform === 'whatsapp' && mail?.canReply === false
  // Dos motivos distintos: el hilo no sabe por que linea entro, o su numero ya
  // no esta conectado. El backend manda el texto; aqui esta la traduccion.
  const readOnlyReason =
    mail?.replyBlockReason ||
    (mail?.phoneNumberId
      ? t('conversations.readonly_disconnected')
      : t('conversations.readonly_no_number'))

  const renderMessageContent = React.useCallback((content: string, isUserMessage: boolean) => {
    if (isUserMessage) {
      return <p className="whitespace-pre-line break-words text-sm text-muted-foreground">{content}</p>
    }

    return (
      <div className="min-w-0 break-words text-sm leading-relaxed text-muted-foreground">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
            h1: ({ children }) => <h1 className="mb-2 mt-3 text-sm font-semibold text-foreground first:mt-0">{children}</h1>,
            h2: ({ children }) => <h2 className="mb-2 mt-3 text-sm font-semibold text-foreground first:mt-0">{children}</h2>,
            h3: ({ children }) => <h3 className="mb-2 mt-3 text-sm font-semibold text-foreground first:mt-0">{children}</h3>,
            h4: ({ children }) => <h4 className="mb-2 mt-3 text-sm font-semibold text-foreground first:mt-0">{children}</h4>,
            h5: ({ children }) => <h5 className="mb-2 mt-3 text-sm font-semibold text-foreground first:mt-0">{children}</h5>,
            h6: ({ children }) => <h6 className="mb-2 mt-3 text-sm font-semibold text-foreground first:mt-0">{children}</h6>,
            ul: ({ children }) => <ul className="mb-2 list-disc space-y-1 pl-5">{children}</ul>,
            ol: ({ children }) => <ol className="mb-2 list-decimal space-y-1 pl-5">{children}</ol>,
            li: ({ children }) => <li>{children}</li>,
            strong: ({ children }) => <strong className="font-semibold text-foreground">{children}</strong>,
            em: ({ children }) => <em className="italic text-foreground">{children}</em>,
            code: ({ children }) => (
              <code className="break-words rounded-md bg-muted px-1 py-0.5 font-mono text-xs text-foreground">{children}</code>
            ),
            pre: ({ children }) => (
              <pre className="mb-2 max-w-full overflow-x-auto rounded-md">{children}</pre>
            ),
            a: ({ href, children }) => (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline-offset-2 hover:underline"
              >
                {children}
              </a>
            ),
            img: ({ src, alt }) => (
              // Keep external images constrained inside the conversation panel.
              <img
                src={src || ""}
                alt={alt || "Message image"}
                loading="lazy"
                className="my-2 max-h-64 w-auto max-w-full rounded-md border object-contain"
              />
            ),
          }}
        >
          {content}
        </ReactMarkdown>
      </div>
    )
  }, [])

  const shouldRenderMessageText = React.useCallback(
    (content: string, attachments: MessageAttachment[]) => {
      if (!content?.trim()) {
        return false
      }
      if (!attachments.length) {
        return true
      }
      const normalized = content.trim().toLowerCase()
      if (/^\[(nota de voz|imagen enviada|vídeo enviado|video enviado|documento enviado|ubicación compartida|sticker)\]$/.test(normalized)) {
        return false
      }
      if (/^\[(documento|imagen enviada):/.test(normalized)) {
        return false
      }
      return true
    },
    []
  )

  return (
    <div className="flex h-full flex-col">
      {!mail && (
        <div className="p-8 text-center text-muted-foreground">{t('functions.no_message_selected')}</div>
      )}
      {mail && (
        <>
          {/* Header (desktop/tablet). Hidden on mobile to keep a single top bar in ConversationsPage. */}
          {!isMobile && (
            <div className="flex items-center justify-between border-b p-2">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className={getBadgeStyles(isPaused)}>
                {mail?.platform === 'phone_call'
                  ? 'Post llamada'
                  : isPaused
                    ? remainingPauseTime === Number.POSITIVE_INFINITY
                      ? t('functions.ai_paused_indefinitely')
                      : remainingPauseTime
                        ? `${t('functions.ai_paused_time')} ${formatTimeRemaining(remainingPauseTime)}`
                        : t('functions.ai_paused')
                    : t('functions.ai_active')}
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              {/* Copy Conversation ID Button - Always visible when conversation is selected */}
              {mail?.id && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon-xs"
                      onClick={handleCopyConversationId}
                    >
                      <Copy className="h-4 w-4" />
                      <span className="sr-only">{t('functions.copy_conversation_id') || 'Copy conversation ID'}</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="font-mono text-sm">{mail.id}</p>
                    <p className="text-xs text-muted-foreground mt-1">{t('functions.copy_conversation_id') || 'Click to copy'}</p>
                  </TooltipContent>
                </Tooltip>
              )}
              {mail?.id && !isMockConversation && (
                <ConversionEventDialog conversationId={mail.id} platform={mail.platform} />
              )}
              {mail?.id && !isMockConversation && (
                <MessengerOutreachActions
                  conversationId={mail.id}
                  platform={mail.platform}
                  onSent={() => void loadMessages(mail.id)}
                />
              )}
              {(() => {
                if (mail?.status === 'active' || (mail?.platform === 'phone_call' && mail?.status !== 'archived')) {
                  // Active conversation or phone call: Show Archive and Delete buttons
                  return (
                    <>
                      <Button
                        variant="ghost"
                        size="icon"
                        disabled={!mail?.id || isArchiving || isMockConversation}
                        onClick={async () => {
                          if (!mail?.id) return
                          try {
                            setIsArchiving(true)

                            if (mail?.platform === 'phone_call') {
                              // Use phone call archive endpoint
                              await conversationsApi.archivePhoneCall(mail.id)
                              // Refresh phone calls list
                              onPhoneCallArchived?.()
                              // Clear selection to show the change in real-time
                              setSelectedConversation({ selected: null })
                            } else {
                              // Use regular conversation archive callback
                              await onArchived?.(mail.id)
                            }

                            toast({ title: 'Conversation archived' })
                          } catch (error) {
                            toast({
                              title: 'Failed to archive conversation',
                              description: error instanceof Error ? error.message : String(error),
                              variant: 'destructive'
                            })
                          } finally {
                            setIsArchiving(false)
                          }
                        }}
                      >
                        <Archive className="h-4 w-4" />
                        <span className="sr-only">Archive</span>
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        disabled={!mail?.id || isDeleting || isMockConversation}
                        onClick={() => setDeleteDialogOpen(true)}
                      >
                        <Trash2 className="h-4 w-4" />
                        <span className="sr-only">Delete</span>
                      </Button>
                    </>
                  )
                } else if (mail?.status === 'archived') {
                  // Archived conversation: Show Activate and Delete buttons
                  return (
                    <>
                      <Button
                        variant="ghost"
                        size="icon"
                        disabled={!mail?.id || isArchiving || isMockConversation}
                        onClick={async () => {
                          if (!mail?.id) return
                          try {
                            setIsArchiving(true)

                            if (mail?.platform === 'phone_call') {
                              // Use phone call unarchive endpoint
                              await conversationsApi.unarchivePhoneCall(mail.id)
                              // Refresh phone calls list
                              onPhoneCallArchived?.()
                              // Clear selection to show the change in real-time
                              setSelectedConversation({ selected: null })
                            } else {
                              // Use regular conversation activate callback
                              await onActivated?.(mail.id)
                            }

                            toast({ title: 'Conversation activated' })
                          } catch (error) {
                            toast({
                              title: 'Failed to activate conversation',
                              description: error instanceof Error ? error.message : String(error),
                              variant: 'destructive'
                            })
                          } finally {
                            setIsArchiving(false)
                          }
                        }}
                      >
                        <Archive className="h-4 w-4 rotate-180" />
                        <span className="sr-only">Activate</span>
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        disabled={!mail?.id || isDeleting || isMockConversation}
                        onClick={() => setDeleteDialogOpen(true)}
                      >
                        <Trash2 className="h-4 w-4" />
                        <span className="sr-only">Delete</span>
                      </Button>
                    </>
                  )
                } else {
                  return null
                }
              })()}
            </div>
            </div>
          )}

          {/* Conmutador de linea: mismo cliente, otro numero de WhatsApp */}
          {mail && (
            <WhatsAppLineSwitch
              mail={mail}
              selectedId={mail.id}
              onSelect={(conversationId) => setSelectedConversation({ selected: conversationId })}
            />
          )}

          {/* Main content area with fixed height and scroll */}
          <div className="flex min-h-0 flex-1 flex-col">
            <ScrollArea ref={scrollAreaRef} className="flex-1">
              <div className="space-y-4 p-4">
                {/* User Intentions section - Removed, now shown in Profile tab */}
                {loadingMessages ? (
                  <div className="flex justify-center py-8">
                    <div className="text-sm text-muted-foreground">{t('functions.loading_messages')}</div>
                  </div>
                ) : conversation.length === 0 ? (
                  <div className="flex flex-col items-center justify-center gap-4 py-8">
                    <div className="text-sm text-muted-foreground">{t('functions.no_messages')}</div>
                    {messagesError && (
                      <div className="max-w-md rounded-md bg-warning/10 px-3 py-2 text-center text-xs text-warning">
                        {t('functions.connection_issue')}
                        <Button
                          variant="link"
                          size="sm"
                          className="ml-1 text-warning"
                          onClick={() => window.location.reload()}
                        >
                          {t('functions.reload_page')}
                        </Button>
                      </div>
                    )}
                  </div>
                ) : (
                  conversation.map((message, index) => (
                    <div
                      key={message.id || `${message.role}-${index}`}
                      // Un aviso de espera («voy a mirarlo») no es la respuesta:
                      // se ve atenuado para que no compita con ella al releer
                      // la conversacion.
                      className={cn(
                        "flex gap-2",
                        isInterimMessage(message.metadata) && "opacity-60",
                      )}
                    >
                      {message.isUserMessage ? (
                        <ContactAvatar
                          className="h-10 w-10 shrink-0"
                          profilePicUrl={mail?.profilePicUrl}
                          email={mail?.email}
                          userId={mail?.user}
                          displayName={message.name}
                        />
                      ) : (
                        <Avatar className="h-10 w-10 shrink-0">
                          <AvatarImage src={message.assistantAvatarSrc} alt={message.name} />
                          <AvatarFallback>
                            {message.name
                              .split(" ")
                              .map((chunk: string) => chunk[0])
                              .join("")}
                          </AvatarFallback>
                        </Avatar>
                      )}
                      <div className="min-w-0 flex-1">
                        {(() => {
                          const deliveryBadge = getDeliveryBadge(message.metadata)

                          return (
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium leading-none">{message.name}</p>
                            {message.email && <p className="truncate text-xs text-muted-foreground" title={message.email}>{message.email}</p>}
                          </div>
                          <div className="flex shrink-0 items-center gap-2">
                            {deliveryBadge ? (
                              <Badge variant="outline" className={deliveryBadge.className}>
                                {deliveryBadge.label}
                              </Badge>
                            ) : null}
                            <p className="text-xs text-muted-foreground">{format(message.timestamp, "HH:mm")}</p>
                          </div>
                        </div>
                          )
                        })()}
                        <div className={cn("mt-2", isInterimMessage(message.metadata) && "italic")}>
                          {shouldRenderMessageText(message.content, message.attachments)
                            ? renderMessageContent(message.content, message.role === "user")
                            : null}
                          <MessageAttachments
                            attachments={message.attachments}
                            attachmentContext={message.role === "user" ? "user" : "assistant"}
                          />
                          {(() => {
                            const deliveryFailure = getDeliveryFailure(message.metadata)
                            if (!deliveryFailure) {
                              return null
                            }

                            const title = deliveryFailure.isTemplate
                              ? 'WhatsApp template failed to deliver'
                              : 'WhatsApp message failed to deliver'
                            const detail = [deliveryFailure.code, deliveryFailure.title]
                              .filter(Boolean)
                              .join(' - ')

                            return (
                              <div className="mt-2 rounded-md border border-destructive/20 bg-destructive/10 px-3 py-2 text-xs text-destructive">
                                <p className="font-medium">{title}</p>
                                {detail ? <p className="mt-1">{detail}</p> : null}
                                {deliveryFailure.message ? <p className="mt-1">{deliveryFailure.message}</p> : null}
                              </div>
                            )
                          })()}
                        </div>
                      </div>
                    </div>
                  ))
                )}

                {/* Show typing indicator when sending message */}
                {sendingMessage && (
                  <div className="flex gap-2">
                    <Avatar>
                      <AvatarImage src="/human-agent-avatar.png" alt={t('functions.agent')} />
                      <AvatarFallback>{t('functions.agent')?.charAt(0) || 'A'}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium leading-none">{t('functions.agent')}</p>
                        <p className="text-xs text-muted-foreground">{t('functions.now')}</p>
                      </div>
                      <div className="mt-2">
                        <p className="animate-pulse text-sm text-muted-foreground">{t('functions.sending')}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>

            {/* Fixed message input at bottom - Hidden for phone calls */}
            {mail?.platform !== 'phone_call' && (
              <div className="mt-auto border-t bg-background p-4">
                <div className="flex flex-col gap-2">
                  {isReadOnlyWhatsAppConversation && (
                    <Alert className="border-warning/40 bg-warning/5">
                      <AlertTitle>Read-only WhatsApp history</AlertTitle>
                      <AlertDescription>{readOnlyReason}</AlertDescription>
                    </Alert>
                  )}
                  <Textarea
                    placeholder={
                      isReadOnlyWhatsAppConversation
                        ? 'This WhatsApp conversation is read-only.'
                        : (t('functions.type_as_agent') || 'Type a message as agent...')
                    }
                    className="min-h-[80px] resize-none"
                    value={messageInput}
                    onChange={(e) => setMessageInput(e.target.value)}
                    disabled={sendingMessage || isReadOnlyWhatsAppConversation}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault()
                        handleSendMessage()
                      }
                    }}
                  />
                  <div className="flex items-center justify-between">
                    {isReadOnlyWhatsAppConversation ? <div /> : (
                      <PauseControl
                        onPause={handlePause}
                        isPaused={isPaused}
                        onResume={handleResume}
                        translations={translations}
                      />
                    )}
                    <div className="flex items-center gap-2">
                      <Button
                        type="submit"
                        onClick={handleSendMessage}
                        disabled={!messageInput.trim() || sendingMessage || isReadOnlyWhatsAppConversation}
                      >
                        <Send className="mr-2 h-4 w-4" />
                        {sendingMessage
                          ? t('functions.sending')
                          : (t('functions.send_as_agent') || 'Send as Agent')
                        }
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          <ConfirmationDialog
            open={deleteDialogOpen}
            onOpenChange={setDeleteDialogOpen}
            title={t('knowledge.delete_confirmation.title')}
            message={t('functions.confirm_delete')}
            description={t('knowledge.delete_confirmation.description')}
            onConfirm={async () => {
              if (!mail?.id) return
              try {
                setIsDeleting(true)

                if (mail?.platform === 'phone_call') {
                  // Use phone call delete endpoint
                  await conversationsApi.deletePhoneCall(mail.id)
                  // Refresh phone calls list
                  onPhoneCallArchived?.()
                } else {
                  // Delegate regular conversation deletion to parent handler to avoid double delete calls
                  if (onDeleted) {
                    await onDeleted(mail.id)
                  } else {
                    await conversationsApi.deleteConversation(mail.id, false)
                  }
                }

                toast({ title: t('functions.deleted_success') || 'Conversation deleted' })
                setDeleteDialogOpen(false)
              } catch (error) {
                toast({
                  title: t('functions.deleted_error') || 'Failed to delete',
                  description: error instanceof Error ? error.message : String(error),
                  variant: 'destructive'
                })
              } finally {
                setIsDeleting(false)
              }
            }}
            onCancel={() => setDeleteDialogOpen(false)}
            loading={isDeleting}
            translations={t}
          />
        </>
      )}
    </div>
  )
}
